import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Dropdown, Table } from 'react-bootstrap';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { UilEllipsisV } from '@iconscout/react-unicons';
import { t } from 'i18next';
import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import { StandardButton } from 'base/StandardButton/StandardButton';
import { StandardLoader } from 'base/Loader/Loader';
import DeleteModel from 'base/DeleteModal/DeleteModal';
import SearchBox from 'components/common/SearchBox';
import AdvanceTable from 'components/base/AdvanceTable';
import CustomAdvanceTableFooter from 'components/customComponent/CustomAdvanceTableFooter';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useAppDispatch } from '../../redux/HooksRedux';
import { RootState } from 'redux/rootReducer';
import {
  ACCENDING,
  DESCENDING,
  ENGLISH,
  modules,
  SELECTED_LANGUAGE,
  ViewAllLimit
} from 'utils/enums/misc';
import SeeMoreText from 'components/base/SeeMoreText';
import { capitalizeFirstLetter, checkAccess } from 'utils/helpers';
import { debounceTime } from 'utils/staticData';
import { GROUP_MANAGEMENT_FORM } from 'constant/RoutesUrls';
import {
  errorNotification,
  successNotification
} from 'base/notifications/Notifications';

import { GroupManagementListI, paramsI } from '../../../types/interface';
import useAdvanceTable from 'hooks/useAdvanceTable';
import * as GroupManagementAction from '../../redux/GroupManagement/GroupManagementAction';
import { getCookie } from 'utils/cookieUtils';

const GroupManagement = () => {
  const userPermission = useSelector(
    (state: RootState) => state.userProfile.permission
  );

  const { totalPages, totalRecords } = useSelector(
    (state: RootState) => state.groupManagementSlice || {}
  );

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentLanguage = getCookie(SELECTED_LANGUAGE);

  const [searchText, setSearchText] = useState('');
  const [pageCount, setPageCount] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isAllVisibleData, setIsAllVisibleData] = useState(false);
  const [sorting, setSorting] = useState<SortingState>();
  const [isLoading, setIsLoading] = useState(true);

  const [groupList, setGroupList] = useState<GroupManagementListI[]>([]);
  const [deleteGroup, setDeleteGroup] = useState({
    id: 0,
    showPopup: false
  });

  const getAllGroups = useCallback(async () => {
    setIsLoading(true);

    const params: paramsI = {
      page: pageCount,
      limit: isAllVisibleData ? ViewAllLimit : limit,
      keyword: searchText,
      sortBy: sorting?.[0]?.id,
      sortOrder: sorting?.[0]?.desc ? DESCENDING : ACCENDING
    };

    try {
      const response = await dispatch(
        GroupManagementAction.getAllGroupManegement(params)
      );

      if (response?.status === 200) {
        setGroupList(response.data.data || []);
      } else {
        errorNotification(response?.data?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, pageCount, limit, isAllVisibleData, searchText, sorting]);

  useEffect(() => {
    const handler = setTimeout(() => {
      getAllGroups();
    }, debounceTime);

    return () => clearTimeout(handler);
  }, [getAllGroups]);

  const handleEdit = (id: number) => {
    navigate(`${GROUP_MANAGEMENT_FORM}?editGroupId=${id}`);
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(
        GroupManagementAction.deleteGroupManagement(deleteGroup.id)
      );
      if (response?.status === 200) {
        successNotification(response.data?.message);
        getAllGroups();
      } else {
        errorNotification(response?.data?.message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
      setDeleteGroup({ id: 0, showPopup: false });
    }
  };

  const columns: ColumnDef<GroupManagementListI>[] = [
    {
      id: 'group_admin_level_number',
      accessorKey: t('group_management.table.column_1'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">
          {t('group_management.table.column_1')}
        </span>
      ),
      cell: ({ row }) => row.index + 1
    },
    {
      id: 'group_English_Name',
      header: () => <span>{t('group_management.table.column_2')}</span>,
      cell: ({ row }) => {
        if (currentLanguage === ENGLISH) {
          return row.original.group_name_en || '-';
        } else {
          return row.original.group_name_de || '-';
        }
      }
    },
    {
      id: 'group_admin_level',
      header: () => <span>{t('group_management.table.column_3')}</span>,
      cell: ({ row }) => {
        const admin_levels = row.original.group_admin_level_mapping || [];
        const elements = [];

        for (let index = 0; index < admin_levels.length; index++) {
          const admin_level = admin_levels[index];
          const stateName =
            currentLanguage === ENGLISH
              ? admin_level.adminLevels.level_name_en
              : admin_level.adminLevels.level_name_de;

          if (index <= 3) {
            elements.push(
              <div key={admin_levels[index].admin_level_id}>{stateName}</div>
            );
          } else if (index === 4) {
            elements.push(
              <SeeMoreText
                headerText={t('group_management.table.column_4')}
                inputType="other"
                maxChars={38}
                key={index}
              >
                <Table bordered className=" border-secondary-subtle" responsive>
                  <tbody>
                    {admin_levels.map((item, idx) => (
                      <tr key={idx}>
                        <td className="text-body ps-2 fs-8">
                          {currentLanguage === ENGLISH
                            ? item.adminLevels.level_name_en
                            : item.adminLevels.level_name_de}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </SeeMoreText>
            );
            break;
          }
        }
        return elements.length > 0 ? elements : '-';
      }
    },
    {
      id: 'group_gederal_state',
      header: () => <span>{t('group_management.table.column_4')}</span>,
      cell: ({ row }) => {
        const states = row.original.group_state_mapping || [];
        const elements = [];

        for (let index = 0; index < states.length; index++) {
          const state = states[index];
          const stateName =
            currentLanguage === ENGLISH
              ? state.states.state_name_en
              : state.states.state_name_de;

          if (index <= 3) {
            elements.push(<div key={state.state_id}>{stateName}</div>);
          } else if (index === 4) {
            elements.push(
              <SeeMoreText
                headerText={t('group_management.table.column_4')}
                inputType="other"
                maxChars={38}
                key={index}
              >
                <Table bordered className=" border-secondary-subtle" responsive>
                  <tbody>
                    {states.map((item, idx) => (
                      <tr key={idx}>
                        <td className="text-body ps-2 fs-8">
                          {currentLanguage === ENGLISH
                            ? item.states.state_name_en
                            : item.states.state_name_de}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </SeeMoreText>
            );
            break;
          }
        }
        return elements.length > 0 ? elements : '-';
      }
    },
    {
      id: 'action',
      header: () =>
        checkAccess(
          [modules.ADMIN_LEVEL.edit, modules.ADMIN_LEVEL.delete],
          userPermission
        ) ? (
          <span>{t('general.common_text.text_8')}</span>
        ) : (
          ''
        ),
      cell: ({ row }) => (
        <ConditionalRender
          condition={checkAccess(
            [modules.ADMIN_LEVEL.edit, modules.ADMIN_LEVEL.delete],
            userPermission
          )}
        >
          <Dropdown
            id="scroll_in_table"
            placement="auto"
            className="position-static"
          >
            <Dropdown.Toggle
              variant="link"
              className="no-padding no-margin ps-0 d-flex"
            >
              <UilEllipsisV className="text-body" />
            </Dropdown.Toggle>

            <Dropdown.Menu className="p-0">
              <ConditionalRender
                condition={checkAccess(
                  [modules.ADMIN_LEVEL.edit],
                  userPermission
                )}
              >
                <Dropdown.Item
                  className="pe-0 border-bottom"
                  onClick={() => handleEdit(row.original.group_id)}
                >
                  <span className="d-flex fs-16 p-0 font-normal">
                    <p className="mb-0">
                      {capitalizeFirstLetter(t('general.common_text.text_13'))}
                    </p>
                  </span>
                </Dropdown.Item>
              </ConditionalRender>

              <ConditionalRender
                condition={checkAccess(
                  [modules.ADMIN_LEVEL.delete],
                  userPermission
                )}
              >
                <Dropdown.Item
                  onClick={() =>
                    setDeleteGroup({
                      id: Number(row.original.group_id),
                      showPopup: true
                    })
                  }
                >
                  <span className="d-flex fs-16 font-normal p-0">
                    <p className="mb-0">
                      {capitalizeFirstLetter(t('general.common_text.text_14'))}
                    </p>
                  </span>
                </Dropdown.Item>
              </ConditionalRender>
            </Dropdown.Menu>
          </Dropdown>
        </ConditionalRender>
      )
    }
  ];

  const table = useAdvanceTable({
    data: groupList || [],
    columns,
    sortable: true,
    pageSize: limit,
    pagination: true,
    sorting,
    setSorting
  });

  const renderDeleteGroup = () => {
    if (deleteGroup.showPopup) {
      return (
        <DeleteModel
          onCancel={() => setDeleteGroup({ id: 0, showPopup: false })}
          onConfirm={handleDelete}
        />
      );
    }
  };

  console.log(deleteGroup);

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between mb-2 align-items-center flex-column flex-md-row gap-3">
        <div className="w-auto">
          <h3 className='m-0'>{capitalizeFirstLetter(t('group_management.title'))}</h3>
        </div>

        <div className="d-flex gap-2 w-auto flex-column flex-md-row">
          <SearchBox
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />

          <ConditionalRender
            condition={checkAccess(
              [modules.ADMIN_LEVEL.create],
              userPermission
            )}
          >
            <StandardButton
              buttonText={t('group_management.button.text_1')}
              onClick={() => navigate(GROUP_MANAGEMENT_FORM)}
            />
          </ConditionalRender>
        </div>
      </div>

      <ConditionalRender condition={!isLoading} fallBack={<StandardLoader />}>
        <div className="px-4 py-1 px-lg-6 bg-body-emphasis overflow-auto scrollbar rounded border-top border-bottom border-left border-right border position-relative top-1">
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              tableProps={{
                className: 'phoenix-table fs-9 p-0',
                responsive: true
              }}
              dataLength={groupList.length}
            />

            <ConditionalRender condition={totalPages > 0}>
              <CustomAdvanceTableFooter
                pagination
                totalPages={totalPages}
                totalRecords={totalRecords}
                recordsPerPage={limit}
                currentPage={pageCount}
                setCurrentPage={setPageCount}
                CanPreviousPage={() => pageCount > 1}
                CanNextPage={() => totalPages > pageCount}
                handleNextPage={() => setPageCount(pageCount + 1)}
                handlePreviousPage={() => setPageCount(pageCount - 1)}
                showViewAllBtn={true}
                isAllVisibleData={isAllVisibleData}
                setIsAllVisibleData={setIsAllVisibleData}
                setRecordsPerPage={setLimit}
                showDataPerPage={totalPages > 1}
                isLoading={isLoading}
              />
            </ConditionalRender>
          </AdvanceTableProvider>
        </div>
      </ConditionalRender>

      {renderDeleteGroup()}
    </>
  );
};

export default GroupManagement;
